"use client";

import { useEffect, useRef } from "react";

import { buildEgressFlights, egressEndsAt, egressLandsAt, egressPoint, type EgressFlight, type EgressGlass, type EgressTarget } from "./egress-flights";
import { RING_COUNT, ringColour } from "./particle-colours";

/*
 * The hero's last beat (Archie, 2026-10-01: "should the particles fly out
 * ... to show them in the agent response?"). As the settled rings give way
 * to the product, some of their particles leave the glass and land on what
 * the answer cites, so the answer is seen to be made of the records the
 * Twin gathered. One short run on a canvas over the stage; each place the
 * particles land on answers with a class of the page's own.
 *
 * The layer mounts as the stage swaps, beside the stage's glass
 * (.gl3d-hole) and its product ([data-egress-product]), and measures both
 * then. The product is still rising into place, so each landing is aimed
 * where its target will rest, not where it is.
 */

/** A kind of place the particles land on. */
export type EgressLanding = {
  /** The places, found in the product. */
  find: (product: HTMLElement) => Element[];
  /** The particles each place takes. */
  count: number;
  /** When the first place's particles start to leave, after the egress starts. */
  leadMs: number;
  /** How much later each further place's leave. */
  stepMs: number;
  /** The class a place wears once its particles have landed. */
  landedClass: string;
};

/** Room round the stage for paths that bow outside it. */
const MARGIN = 64;
/** The least of a place that must show for particles to land on it. */
const SHOWING = 0.6;

type Box = { left: number; top: number; right: number; bottom: number };

/** The canvas's place on the page: its corner in the window, and its size. */
type Sheet = { originX: number; originY: number; width: number; height: number };

/** A place's answer to its particles: the class it takes, and when. */
type Answer = { element: Element; landedClass: string; at: number; done: boolean };

/**
 * What shows of a place in the product: its box, cut by every ancestor
 * that clips its overflow (a frame shorter than its answer hides the foot
 * of it) and by the window's right edge, where the product runs off the
 * page. Nothing if too little of it shows to land on.
 */
function showingBox(element: Element, product: HTMLElement): Box | null {
  const whole = element.getBoundingClientRect();
  if (whole.width <= 0 || whole.height <= 0) return null;
  const box: Box = { left: whole.left, top: whole.top, right: Math.min(whole.right, document.documentElement.clientWidth), bottom: whole.bottom };
  for (let node = element.parentElement; node && node !== product.parentElement; node = node.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(node);
    const clip = node.getBoundingClientRect();
    if (overflowX !== "visible") {
      box.left = Math.max(box.left, clip.left);
      box.right = Math.min(box.right, clip.right);
    }
    if (overflowY !== "visible") {
      box.top = Math.max(box.top, clip.top);
      box.bottom = Math.min(box.bottom, clip.bottom);
    }
  }
  const showing = Math.max(0, box.right - box.left) * Math.max(0, box.bottom - box.top);
  return showing >= whole.width * whole.height * SHOWING ? box : null;
}

/** Maps a point of the product, as measured now, to where it rests once the product has arrived (its transform gone). */
function arrivalOf(product: HTMLElement): (x: number, y: number) => { x: number; y: number; scale: number } {
  const matrix = new DOMMatrixReadOnly(getComputedStyle(product).transform);
  const rect = product.getBoundingClientRect();
  const centreX = rect.left + rect.width / 2;
  const centreY = rect.top + rect.height / 2;
  const scale = matrix.a || 1;
  return (x, y) => ({ x: centreX - matrix.e + (x - centreX) / scale, y: centreY - matrix.f + (y - centreY) / scale, scale });
}

/** Lays the canvas over the stage and whatever of the product runs past it, to the window's edge. */
function laySheet(canvas: HTMLCanvasElement, stage: HTMLElement, context: CanvasRenderingContext2D): Sheet {
  const stageRect = stage.getBoundingClientRect();
  const originX = stageRect.left - MARGIN;
  const originY = stageRect.top - MARGIN;
  const width = Math.max(stageRect.right + MARGIN, document.documentElement.clientWidth) - originX;
  const height = stageRect.height + MARGIN * 2;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.style.left = `${-MARGIN}px`;
  canvas.style.top = `${-MARGIN}px`;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { originX, originY, width, height };
}

/** The places that show, as targets on the sheet where they will rest, each with its answer. A place that does not show takes no particles, and the ones after it close up. */
function findTargets(landings: readonly EgressLanding[], product: HTMLElement, sheet: Sheet): { targets: EgressTarget[]; answers: Answer[] } {
  const arrival = arrivalOf(product);
  const targets: EgressTarget[] = [];
  const answers: Answer[] = [];
  for (const landing of landings) {
    let index = 0;
    for (const element of landing.find(product)) {
      const box = showingBox(element, product);
      if (!box) continue;
      const rest = arrival(box.left, box.top);
      const target: EgressTarget = {
        x: rest.x - sheet.originX,
        y: rest.y - sheet.originY,
        width: (box.right - box.left) / rest.scale,
        height: (box.bottom - box.top) / rest.scale,
        count: landing.count,
        leadMs: landing.leadMs + index * landing.stepMs,
      };
      targets.push(target);
      answers.push({ element, landedClass: landing.landedClass, at: egressLandsAt(target), done: false });
      index += 1;
    }
  }
  return { targets, answers };
}

/** The glass on the sheet, as the rings last stood in it. */
function glassOn(sheet: Sheet, glass: HTMLElement): EgressGlass {
  const rect = glass.getBoundingClientRect();
  return { x: rect.left + rect.width / 2 - sheet.originX, y: rect.top + rect.height / 2 - sheet.originY, radius: Math.min(rect.width, rect.height) / 2 };
}

/** Draws every particle in flight at `at`: a particle keeps its ring's colour, so one fill a ring draws them all. */
function drawFlights(context: CanvasRenderingContext2D, sheet: Sheet, byRing: readonly EgressFlight[][], at: number) {
  context.clearRect(0, 0, sheet.width, sheet.height);
  byRing.forEach((flights, ring) => {
    context.beginPath();
    for (const flight of flights) {
      const point = egressPoint(flight, at);
      if (!point) continue;
      context.moveTo(point.x + point.radius, point.y);
      context.arc(point.x, point.y, point.radius, 0, Math.PI * 2);
    }
    context.fillStyle = ringColour(ring);
    context.fill();
  });
}

export function EvidenceEgress({ landings, className }: { landings: readonly EgressLanding[]; className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = canvas?.parentElement;
    const glass = stage?.querySelector<HTMLElement>(".gl3d-hole");
    const product = stage?.querySelector<HTMLElement>("[data-egress-product]");
    const context = canvas?.getContext("2d");
    if (!canvas || !stage || !glass || !product || !context) {
      console.error("[glass-twin] Egress stage, glass or product is unavailable");
      return;
    }

    const sheet = laySheet(canvas, stage, context);
    const { targets, answers } = findTargets(landings, product, sheet);
    if (targets.length === 0) return;
    const byRing: EgressFlight[][] = Array.from({ length: RING_COUNT }, () => []);
    for (const flight of buildEgressFlights(glassOn(sheet, glass), targets)) byRing[flight.ring]?.push(flight);
    const endsAt = egressEndsAt(targets);

    let frame = 0;
    let startedAt: number | null = null;
    const draw = (now: number) => {
      startedAt ??= now;
      const at = now - startedAt;
      drawFlights(context, sheet, byRing, at);
      for (const answer of answers) {
        if (answer.done || at < answer.at) continue;
        answer.done = true;
        if (answer.landedClass) answer.element.classList.add(answer.landedClass);
      }
      if (at < endsAt) {
        frame = requestAnimationFrame(draw);
        return;
      }
      // Done: give the canvas's memory back; the classes stay until the layer leaves the page.
      frame = 0;
      canvas.width = 0;
      canvas.height = 0;
    };
    frame = requestAnimationFrame(draw);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      for (const answer of answers) if (answer.landedClass) answer.element.classList.remove(answer.landedClass);
    };
  }, [landings]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
